import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import StatusChip from '../components/shared/StatusChip';
import PageHeader from '../../../components/common/PageHeader';
import EmptyState from '../../../components/common/EmptyState';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import PAMMErrorState from '../components/shared/PAMMErrorState';
import { DataTable } from '../../../components/ui/data-table';
import { pammAllocationService } from '../services/pammService';
import { formatCurrency, formatDate, exportToCSV } from '../utils';
import type { AllocationRecord } from '../types';
import type { DataTableColumn } from '../../../components/ui/data-table';

export default function AllocationHistory() {
  const [records, setRecords] = useState<AllocationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string }>(
    {}
  );

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      setRecords(await pammAllocationService.getAll());
    } catch {
      setError('Failed to load allocation history.');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
  }, []);

  const columns: DataTableColumn<AllocationRecord>[] = [
    {
      id: 'fund',
      header: 'Fund',
      accessor: row => (
        <span className="text-xs font-medium">{row.fundName}</span>
      ),
      sortable: true,
      sortValue: row => row.fundName,
    },
    {
      id: 'investor',
      header: 'Investor',
      accessor: row => <span className="text-xs">{row.investorName}</span>,
      sortable: true,
      sortValue: row => row.investorName,
    },
    {
      id: 'type',
      header: 'Type',
      accessor: row => <StatusChip status={row.type} size="sm" />,
    },
    {
      id: 'prev',
      header: 'Previous %',
      accessor: row => (
        <span className="text-xs">{row.previousAllocation.toFixed(2)}%</span>
      ),
    },
    {
      id: 'new',
      header: 'New %',
      accessor: row => (
        <span className="text-xs font-medium">
          {row.newAllocation.toFixed(2)}%
        </span>
      ),
    },
    {
      id: 'amount',
      header: 'Amount',
      accessor: row => (
        <span className="text-xs font-medium">
          {formatCurrency(row.amount)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.amount,
    },
    {
      id: 'date',
      header: 'Date',
      accessor: row => (
        <span className="text-xs text-muted-foreground">
          {formatDate(row.date)}
        </span>
      ),
      sortable: true,
      sortValue: row => row.date,
    },
    {
      id: 'reason',
      header: 'Reason',
      accessor: row => (
        <span className="text-xs text-muted-foreground truncate max-w-[180px] block">
          {row.reason ?? '—'}
        </span>
      ),
    },
    {
      id: 'by',
      header: 'Processed By',
      accessor: row => <span className="text-xs">{row.processedBy}</span>,
    },
  ];

  if (loading)
    return (
      <div className="space-y-4">
        <PageHeader title="Allocation History" />
        <LoadingSkeleton type="table" />
      </div>
    );
  if (error)
    return (
      <div className="space-y-4">
        <PageHeader title="Allocation History" />
        <PAMMErrorState message={error} onRetry={load} />
      </div>
    );

  return (
    <div className="space-y-6 pb-6">
      <PageHeader
        title="Allocation History"
        description="Complete record of all investor allocation changes"
      />
      {records.length === 0 ? (
        <EmptyState
          icon={History}
          title="No Allocation Records"
          description="Allocation changes will appear here."
        />
      ) : (
        <DataTable
          columns={columns}
          data={records}
          searchValue={searchValue}
          showColumnVisibility={true}
          searchPlaceholder="Search allocation history..."
          onSearchChange={setSearchValue}
          onSearchReset={() => setSearchValue('')}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
          showExportButton={true}
          exportButtonText="Export CSV"
          onExportClick={() => exportToCSV(records, 'allocation_history')}
        />
      )}
    </div>
  );
}
